import { Controller } from '@hotwired/stimulus'

/**
 * FAQ List Controller
 *
 * Manages show more/less functionality for FAQ lists.
 * Initially shows a limited number of FAQs and allows expanding to show all.
 *
 * @controller faq-list
 * @target {HTMLElement} item - FAQ item elements to show/hide
 * @target {HTMLElement} toggle - Toggle button element
 * @value {Number} limit - Number of items to show initially (default: 6)
 *
 * @example
 * <div data-controller="faq-list" data-faq-list-limit-value="6">
 *   <div data-faq-list-target="item">FAQ 1</div>
 *   <div data-faq-list-target="item">FAQ 2</div>
 *   ...
 *   <button data-faq-list-target="toggle" data-action="faq-list#toggle">
 *     Show More
 *   </button>
 * </div>
 */
export default class extends Controller {
  static targets = ['item', 'toggle', 'count']
  static values = {
    limit: { type: Number, default: 6 },
    expanded: { type: Boolean, default: false }
  }

  connect() {
    this.updateVisibility()
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    this.expandedValue = !this.expandedValue
    this.updateVisibility()
  }

  updateVisibility() {
    const totalItems = this.itemTargets.length
    const hiddenCount = totalItems - this.limitValue

    this.itemTargets.forEach((item, index) => {
      if (this.expandedValue || index < this.limitValue) {
        item.classList.remove('d-none')
        item.removeAttribute('aria-hidden')
      } else {
        item.classList.add('d-none')
        item.setAttribute('aria-hidden', 'true')
      }
    })

    // Update toggle button text, visibility, and ARIA state
    if (this.hasToggleTarget) {
      // Keep aria-expanded in sync with actual state
      this.toggleTarget.setAttribute('aria-expanded', this.expandedValue.toString())

      if (hiddenCount <= 0) {
        this.toggleTarget.classList.add('d-none')
      } else {
        this.toggleTarget.classList.remove('d-none')
        const icon = this.toggleTarget.querySelector('i')
        const text = this.toggleTarget.querySelector('[data-text]')

        if (this.expandedValue) {
          if (text) text.textContent = 'Show Less'
          if (icon) {
            icon.classList.remove('fa-chevron-down')
            icon.classList.add('fa-chevron-up')
          }
        } else {
          if (text) text.textContent = `Show ${hiddenCount} More`
          if (icon) {
            icon.classList.remove('fa-chevron-up')
            icon.classList.add('fa-chevron-down')
          }
        }
      }
    }

    // Update count display
    if (this.hasCountTarget) {
      if (this.expandedValue) {
        this.countTarget.textContent = `Showing all ${totalItems} questions`
      } else {
        this.countTarget.textContent = `Showing ${Math.min(this.limitValue, totalItems)} of ${totalItems} questions`
      }
    }
  }
}

